/**
 * Styled components for CartDrawer (desktop slide-in overlay).
 *
 * Self-check:
 * - Desktop-only: Overlay and DrawerPanel are hidden on mobile via ${mobile} { display: none; }
 * - All z-indexes: Overlay=1299 (transparent click-shield), DrawerPanel=1300 (visible surface)
 * - Width: clamp(320px, 28vw, 392px) — keeps more of the product grid visible
 * - No backdrop dimming (D-02): Overlay background is transparent
 * - All tokens from theme (no raw colors)
 */

import styled, { keyframes } from "styled-components";
import { mobile } from "../../ui/media";
import { Button } from "../../ui/Button";

// ── Badge pulse animation (exported for use in Plan 02 navbar badge) ──
export const badgePulse = keyframes`
  0%   { transform: scale(1); }
  40%  { transform: scale(1.35); }
  100% { transform: scale(1); }
`;

// ── Overlay ─────────────────────────────────────────────────────────────────
// Right-edge fixed container so the rest of the page remains fully scrollable
// and clickable while the desktop cart is open.
export const Overlay = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: clamp(320px, 28vw, 392px);
  max-width: min(100vw, 392px);
  z-index: 1299;
  background: transparent;
  display: flex;
  justify-content: flex-end;
  pointer-events: none;

  ${mobile} {
    display: none;
  }
`;

// ── DrawerPanel ──────────────────────────────────────────────────────────────
// The actual drawer container that slides in from the right edge.
export const DrawerPanel = styled.div`
  width: 100%;
  max-width: min(100vw, 392px);
  height: 100%;
  background: ${({ theme }) => theme.colors.background.liquidGlass};
  backdrop-filter: blur(32px) saturate(180%) brightness(1.05);
  -webkit-backdrop-filter: blur(32px) saturate(180%) brightness(1.05);
  border-left: 1px solid ${({ theme }) => theme.colors.background.liquidGlassBorder};
  box-shadow: ${({ theme }) => theme.colors.background.liquidGlassHighlight},
    -8px 0 32px ${({ theme }) => theme.colors.shadow.medium};
  border-radius: ${({ theme }) => theme.radius.xl}px 0 0
    ${({ theme }) => theme.radius.xl}px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  pointer-events: auto;

  ${mobile} {
    display: none;
  }
`;

// ── DrawerHeader ─────────────────────────────────────────────────────────────
export const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(2)}`};
  background: ${({ theme }) => theme.colors.background.liquidGlass};
  backdrop-filter: blur(24px) saturate(160%);
  -webkit-backdrop-filter: blur(24px) saturate(160%);
  border-bottom: 1px solid ${({ theme }) => theme.colors.background.liquidGlassBorder};
  flex-shrink: 0;
`;

// ── DrawerTitle ──────────────────────────────────────────────────────────────
export const DrawerTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.h3};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

// ── CloseButton ──────────────────────────────────────────────────────────────
// Matches the IconButton pattern from Navbar.styled.tsx.
export const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ theme }) => theme.colors.background.liquidGlassBorder};
  background: ${({ theme }) => theme.colors.background.liquidGlass};
  backdrop-filter: blur(16px) saturate(160%);
  -webkit-backdrop-filter: blur(16px) saturate(160%);
  color: ${({ theme }) => theme.colors.text.primary};
  border-radius: ${({ theme }) => theme.radius.pill}px;
  width: ${({ theme }) => theme.spacing(4.5)};
  height: ${({ theme }) => theme.spacing(4.5)};
  cursor: pointer;
  transition: background ${({ theme }) => theme.motion.fast} ease;
  flex-shrink: 0;

  &:hover {
    background: ${({ theme }) => theme.colors.background.glass};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 ${({ theme }) => theme.spacing(0.5)}
        ${({ theme }) => theme.colors.background.surfaceAlt},
      0 0 0 ${({ theme }) => theme.spacing(1)}
        ${({ theme }) => theme.colors.border.focus};
  }
`;

// ── ItemList ─────────────────────────────────────────────────────────────────
// Scrollable list of cart items — grows to fill available space.
export const ItemList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing(2)};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

// ── ItemRow ──────────────────────────────────────────────────────────────────
export const ItemRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(2)};
  position: relative;
`;

// ── ItemThumb ────────────────────────────────────────────────────────────────
export const ItemThumb = styled.img`
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.radius.md}px;
  object-fit: cover;
  flex-shrink: 0;
`;

// ── ItemInfo ─────────────────────────────────────────────────────────────────
export const ItemInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(0.5)};
  min-width: 0;
`;

// ── ItemName ─────────────────────────────────────────────────────────────────
export const ItemName = styled.p`
  font-size: ${({ theme }) => theme.typography.body};
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0;
`;

// ── ItemPrice ────────────────────────────────────────────────────────────────
export const ItemPrice = styled.p`
  font-size: ${({ theme }) => theme.typography.body};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
  flex-shrink: 0;
`;

// ── QtyControls ──────────────────────────────────────────────────────────────
export const QtyControls = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
`;

// ── QtyBtn ───────────────────────────────────────────────────────────────────
// 32px circle matching Cart.styled.tsx QuantityButton pattern.
export const QtyBtn = styled.button`
  width: ${({ theme }) => theme.spacing(4)};
  height: ${({ theme }) => theme.spacing(4)};
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  background: ${({ theme }) => theme.colors.background.surfaceAlt};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.body};
  font-weight: 600;
  cursor: pointer;
  transition: background ${({ theme }) => theme.motion.fast} ease;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.state.hover};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 ${({ theme }) => theme.spacing(0.5)}
        ${({ theme }) => theme.colors.background.surfaceAlt},
      0 0 0 ${({ theme }) => theme.spacing(1)}
        ${({ theme }) => theme.colors.border.focus};
  }

  &:disabled {
    cursor: not-allowed;
    color: ${({ theme }) => theme.colors.text.disabled};
    border-color: ${({ theme }) => theme.colors.border.subtle};
    background: ${({ theme }) => theme.colors.background.surface};
  }
`;

// ── QtyValue ─────────────────────────────────────────────────────────────────
export const QtyValue = styled.span`
  min-width: ${({ theme }) => theme.spacing(2.5)};
  text-align: center;
  font-weight: 600;
  font-size: ${({ theme }) => theme.typography.small};
`;

// ── RemoveBtn ────────────────────────────────────────────────────────────────
// Icon-only trash/remove button positioned absolutely at top-right of ItemRow.
export const RemoveBtn = styled.button`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.accent.danger};
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing(0.5)};
  position: absolute;
  top: 0;
  right: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.radius.sm}px;
  transition: background ${({ theme }) => theme.motion.fast} ease,
    color ${({ theme }) => theme.motion.fast} ease;

  &:hover {
    background: ${({ theme }) => theme.colors.accent.danger};
    color: ${({ theme }) => theme.colors.text.inverse};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 ${({ theme }) => theme.spacing(0.5)}
        ${({ theme }) => theme.colors.background.surfaceAlt},
      0 0 0 ${({ theme }) => theme.spacing(1)}
        ${({ theme }) => theme.colors.border.destructive};
  }
`;

// ── DrawerFooter ─────────────────────────────────────────────────────────────
export const DrawerFooter = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.background.liquidGlassBorder};
  background: ${({ theme }) => theme.colors.background.liquidGlass};
  backdrop-filter: blur(24px) saturate(160%);
  -webkit-backdrop-filter: blur(24px) saturate(160%);
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(2)}`};
  flex-shrink: 0;
`;

// ── SubtotalRow ──────────────────────────────────────────────────────────────
export const SubtotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing(1.5)};
`;

// ── SubtotalLabel ────────────────────────────────────────────────────────────
export const SubtotalLabel = styled.p`
  font-size: ${({ theme }) => theme.typography.body};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

// ── SubtotalValue ────────────────────────────────────────────────────────────
export const SubtotalValue = styled.p`
  font-size: ${({ theme }) => theme.typography.body};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

// ── SubtotalNote ─────────────────────────────────────────────────────────────
export const SubtotalNote = styled.p`
  font-size: ${({ theme }) => theme.typography.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0 0 ${({ theme }) => theme.spacing(2)} 0;
`;

// ── CheckoutBtn ──────────────────────────────────────────────────────────────
export const CheckoutBtn = styled(Button)`
  width: 100%;
  min-height: ${({ theme }) => theme.spacing(6)};
`;

// ── EmptyWrap ────────────────────────────────────────────────────────────────
export const EmptyWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(4)};
  text-align: center;
`;

// ── EmptyHeading ─────────────────────────────────────────────────────────────
export const EmptyHeading = styled.h3`
  font-size: ${({ theme }) => theme.typography.h3};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

// ── EmptyBody ────────────────────────────────────────────────────────────────
export const EmptyBody = styled.p`
  font-size: ${({ theme }) => theme.typography.body};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
  text-align: center;
`;

// ── RecsSection ──────────────────────────────────────────────────────────────
// Placeholder section for "You might also like" recommendations (CART-05, Phase 20).
export const RecsSection = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.border.subtle};
  padding: ${({ theme }) => theme.spacing(2)};
  flex-shrink: 0;
`;

// ── RecsHeading ──────────────────────────────────────────────────────────────
export const RecsHeading = styled.p`
  font-size: ${({ theme }) => theme.typography.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 0 0 ${({ theme }) => theme.spacing(1.5)} 0;
`;

// ── RecsGrid ─────────────────────────────────────────────────────────────────
export const RecsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing(1)};
`;

// ── RecsSkeleton ─────────────────────────────────────────────────────────────
// Skeleton placeholder card for recommendations (aria-busy for screen readers).
export const RecsSkeleton = styled.div`
  width: 100%;
  aspect-ratio: 1;
  border-radius: ${({ theme }) => theme.radius.md}px;
  background: ${({ theme }) => theme.colors.background.surfaceAlt};
`;

export const RecCard = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  border-radius: ${({ theme }) => theme.radius.md}px;
  background: ${({ theme }) => theme.colors.background.surface};
  border: 1px solid ${({ theme }) => theme.colors.border.subtle};
  overflow: hidden;
  cursor: pointer;
  transition: transform ${({ theme }) => theme.motion.fast} ease,
    box-shadow ${({ theme }) => theme.motion.fast} ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.colors.shadow.low};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 ${({ theme }) => theme.spacing(0.5)}
        ${({ theme }) => theme.colors.background.surfaceAlt},
      0 0 0 ${({ theme }) => theme.spacing(1)}
        ${({ theme }) => theme.colors.border.focus};
  }
`;

export const RecThumb = styled.img`
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  background: ${({ theme }) => theme.colors.background.surfaceAlt};
`;

export const RecInfo = styled.div`
  padding: ${({ theme }) => `${theme.spacing(0.75)} ${theme.spacing(1)}`};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(0.5)};
  flex: 1;
`;

export const RecName = styled.p`
  font-size: ${({ theme }) => theme.typography.small};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const RecPrice = styled.p`
  font-size: ${({ theme }) => theme.typography.small};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

export const RecAddBtn = styled.button`
  width: 100%;
  padding: ${({ theme }) => theme.spacing(0.75)};
  background: ${({ theme }) => theme.colors.accent.primary};
  color: ${({ theme }) => theme.colors.text.inverse};
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.border.subtle};
  font-size: ${({ theme }) => theme.typography.small};
  font-weight: 600;
  cursor: pointer;

  &:hover {
    opacity: 0.92;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.border.focus};
    outline-offset: -2px;
  }
`;
